import API_BASE from '../config';
import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, Search, X, CheckCircle2,
  Loader2, Trash2, AlertTriangle, Clock, FileText, Shield, ChevronRight
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import UpgradeGate from "../components/UpgradeGate";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Vendor {
  id:                       number;
  name:                     string;
  vendor_type:              string;
  has_soc2:                 boolean;
  has_iso27001:             boolean;
  gdpr_compliant:           boolean;
  recent_breach:            boolean;
  sub_processors_disclosed: boolean;
  patch_management_policy:  boolean;
  breach_notification_sla:  string;
  score:                    number;
  status:                   "low" | "medium" | "high" | "critical";
  created_at:               string;
}
// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusStyle: Record<string, string> = {
  low:      "bg-emerald-50 text-emerald-700 border-emerald-100",
  medium:   "bg-amber-50 text-amber-700 border-amber-100",
  high:     "bg-red-50 text-red-700 border-red-100",
  critical: "bg-red-100 text-red-800 border-red-200",
};

const barColor = (score: number) =>
  score > 75 ? "bg-emerald-500" : score > 50 ? "bg-amber-500" : "bg-red-500";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Vendors() {
  const queryClient = useQueryClient();

  const [isAddOpen,    setIsAddOpen]    = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [editTarget,   setEditTarget]   = useState<Vendor | null>(null);
  const [intelTarget,  setIntelTarget]  = useState<Vendor | null>(null);
  const [intelData,    setIntelData]    = useState<any>(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelOpen,    setIntelOpen]    = useState(false);
  const [addError,     setAddError]     = useState("");

  const [newVendor, setNewVendor] = useState({
    name:                     "",
    vendor_type:              "Software",
    has_soc2:                 false,
    has_iso27001:             false,
    gdpr_compliant:           false,
    recent_breach:            false,
    sub_processors_disclosed: false,
    patch_management_policy:  false,
    breach_notification_sla:  "unknown",
  });

const [editVendor, setEditVendor] = useState({
    name:                     "",
    vendor_type:              "Software",
    has_soc2:                 false,
    has_iso27001:             false,
    gdpr_compliant:           false,
    recent_breach:            false,
    sub_processors_disclosed: false,
    patch_management_policy:  false,
    breach_notification_sla:  "unknown",
  });

  // Fetch user profile for tier check

  // Fetch user profile for tier check
  const { data: userData } = useQuery({
    queryKey: ["user-profile"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/auth/me`, { headers: authHeaders() })
           .then((res) => res.data),
    enabled: !!localStorage.getItem("token"),
  });

  const tier = userData?.subscription_tier?.toLowerCase() || "standard";
  const isProfessional = tier === "professional" || tier === "enterprise";
  const isTrialPreview = tier === "standard";

  const handleIntelligence = useCallback(async (vendor: Vendor) => {
    if (!isProfessional) {
      window.location.href = '/reports';
      return;
    }
    setIntelTarget(vendor);
    setIntelData(null);
    setIntelLoading(true);
    setIntelOpen(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE}/api/vendors/${vendor.id}/intelligence`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIntelData(res.data);
    } catch {
      setIntelData(null);
    } finally {
      setIntelLoading(false);
    }
  }, [isProfessional]);

  // Fetch vendors
  // Get selected entity from localStorage
  const selectedEntity = localStorage.getItem("selectedEntity") || "";

  const { data: vendors = [], isLoading } = useQuery<Vendor[]>({
    queryKey: ["vendors", selectedEntity],
    queryFn:  () => {
      const url = selectedEntity
        ? `${API_BASE}/api/vendors?entity=${encodeURIComponent(selectedEntity)}`
        : `${API_BASE}/api/vendors`;
      return axios.get(url, { headers: authHeaders() }).then((res) => res.data);
    },
    refetchOnWindowFocus: true,
    enabled: !!localStorage.getItem("token"),
    staleTime: 2 * 60 * 1000,
  });

  // Add mutation
  const addMutation = useMutation({
    mutationFn: (payload: typeof newVendor) =>
      axios.post(`${API_BASE}/api/vendors`,
        { ...payload, company_name: selectedEntity },
        { headers: authHeaders() }
      ).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      setIsAddOpen(false);
      setNewVendor({
        name: "", vendor_type: "Software",
        has_soc2: false, has_iso27001: false,
        gdpr_compliant: false, recent_breach: false,
        sub_processors_disclosed: false, patch_management_policy: false,
        breach_notification_sla: "unknown",
      });
    },
    onError: (err: any) => {
      setAddError(err?.response?.data?.detail || "Failed to add vendor. Please try again.");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      axios.delete(`${API_BASE}/api/vendors/${id}`, { headers: authHeaders() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      setDeleteTarget(null);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: typeof editVendor }) =>
      axios.put(`${API_BASE}/api/vendors/${id}`,
        payload,
        { headers: authHeaders() }
      ).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      setEditTarget(null);
    },
  });

  const openEdit = (vendor: Vendor) => {
    setEditTarget(vendor);
    setEditVendor({
      name:                     vendor.name,
      vendor_type:              vendor.vendor_type,
      has_soc2:                 vendor.has_soc2,
      has_iso27001:             vendor.has_iso27001,
      gdpr_compliant:           vendor.gdpr_compliant,
      recent_breach:            vendor.recent_breach,
      sub_processors_disclosed: vendor.sub_processors_disclosed || false,
      patch_management_policy:  vendor.patch_management_policy  || false,
      breach_notification_sla:  vendor.breach_notification_sla  || "unknown",
    });
  };

  // Filter
  const filtered = useMemo(() =>
    vendors.filter((v) =>
      v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.vendor_type.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [vendors, searchQuery]
  );

  // Stats
  const totalMonitored = vendors.length;
  const criticalCount  = vendors.filter(v => v.status === "critical" || v.status === "high").length;
  const avgScore       = totalMonitored > 0
    ? Math.round(vendors.reduce((acc, v) => acc + v.score, 0) / totalMonitored)
    : 0;

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <Loader2 size={36} className="animate-spin text-red-600" />
    </div>
  );

  // Trial users see vendor page with 1 vendor limit

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 md:p-6 space-y-4 bg-[#f8fafc] min-h-screen font-['Inter']"
    >
      {/* Trial Preview Banner */}
      {isTrialPreview && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-black text-amber-800 uppercase tracking-widest">Trial Preview — 1 Vendor Limit</p>
              <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                You can add 1 vendor on your trial. Upgrade to Professional for full vendor risk matrix.
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/pricing'}
            className="flex-shrink-0 px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all"
          >
            Upgrade →
          </button>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        <div className="text-left">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-1">
            Third-Party Intelligence
          </p>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase">
            Vendor Risk Management
          </h1>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
            Third-Party Security Intelligence
          </p>
        </div>
        <button
          onClick={() => setIsAddOpen(true)}
          className="bg-red-600 text-white px-6 py-3.5 rounded-2xl text-[10px]
            font-black uppercase tracking-widest shadow-lg shadow-red-100
            flex items-center gap-2.5 hover:bg-slate-900 transition-all active:scale-95
            flex-shrink-0"
        >
          <Plus size={16} strokeWidth={3} /> Add New Vendor
        </button>
      </div>

      {/* ── STATS HERO ──────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
        rounded-[3rem] p-8 shadow-2xl border border-white/5">

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
              Supply Chain Risk
            </p>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight">
              Vendor Risk Matrix
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
            <p className="text-4xl font-black text-white">{totalMonitored}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">Total Monitored</p>
          </div>
          <div className={`rounded-2xl p-5 text-center border ${
            criticalCount > 0 ? "bg-red-900/30 border-red-800/30" : "bg-white/5 border-white/10"
          }`}>
            <p className={`text-4xl font-black ${criticalCount > 0 ? "text-red-400" : "text-white"}`}>
              {criticalCount}
            </p>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">High Risk / Critical</p>
          </div>
          <div className={`rounded-2xl p-5 text-center border ${
            avgScore < 50 ? "bg-amber-900/30 border-amber-800/30" : "bg-emerald-900/30 border-emerald-800/30"
          }`}>
            <p className={`text-4xl font-black ${avgScore < 50 ? "text-amber-400" : "text-emerald-400"}`}>
              {avgScore}
            </p>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">Avg. Security Score</p>
          </div>
        </div>
      </div>

      {/* ── VENDOR TABLE ────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[2.5rem] border border-white/10 shadow-2xl
        overflow-hidden text-left">

        {/* Search */}
        <div className="p-6 border-b border-white/10">
          <div className="relative max-w-sm">
            <Search size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search vendor registry..."
              className="w-full pl-11 pr-5 py-3.5 bg-white/5 border border-white/10
                rounded-2xl text-sm font-bold text-white placeholder:text-slate-500
                placeholder:font-normal focus:outline-none focus:border-red-500
                transition-all"
            />
          </div>
        </div>

        {/* Empty state */}
        {vendors.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center
              justify-center mx-auto mb-5 border-2 border-dashed border-white/20">
              <Users size={28} className="text-slate-400" />
            </div>
            <h3 className="text-base font-black text-white uppercase tracking-tight mb-1">
              No Vendors Registered
            </h3>
            <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mb-6">
              Add your first vendor to begin third-party risk monitoring
            </p>
            <button
              onClick={() => setIsAddOpen(true)}
              className="bg-red-600 text-white px-7 py-3.5 rounded-2xl text-[10px]
                font-black uppercase tracking-widest shadow-lg shadow-red-100
                inline-flex items-center gap-2.5 hover:bg-slate-900 transition-all"
            >
              <Plus size={16} strokeWidth={3} /> Add First Vendor
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-14 text-center">
            <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">
              No vendors match "{searchQuery}"
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 text-[9px] font-black uppercase tracking-[0.2em]
                  text-slate-300 border-b border-white/10">
                  <th className="px-8 py-4 text-left">Vendor Entity</th>
                  <th className="px-8 py-4 text-left">Type</th>
                  <th className="px-8 py-4 text-center">Risk Status</th>
                  <th className="px-8 py-4 text-center">Score</th>
                  <th className="px-8 py-4 text-center">SOC2</th>
                  <th className="px-8 py-4 text-center">ISO 27001</th>
                  <th className="px-8 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((vendor) => (
                  <motion.tr
                    key={vendor.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-white/5 transition-colors group"
                  >
                    <td className="px-8 py-5 font-black text-white text-sm uppercase
                      tracking-tight">
                      {vendor.name}
                    </td>
                    <td className="px-8 py-5 text-slate-300 text-[10px] font-black uppercase
                      tracking-widest">
                      {vendor.vendor_type}
                    </td>
                    <td className="px-8 py-5 text-center">
                      <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase
                        tracking-widest border ${statusStyle[vendor.status]}`}>
                        {vendor.status}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-sm font-black text-white w-8 text-right">
                          {vendor.score}
                        </span>
                        <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${vendor.score}%` }}
                            transition={{ duration: 0.5 }}
                            className={`h-full ${barColor(vendor.score)}`}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-center">
                      {vendor.has_soc2
                        ? <CheckCircle2 size={16} className="text-emerald-500 mx-auto" />
                        : <X size={16} className="text-red-400 mx-auto" />
                      }
                    </td>
                    <td className="px-8 py-5 text-center">
                      {vendor.has_iso27001
                        ? <CheckCircle2 size={16} className="text-emerald-500 mx-auto" />
                        : <X size={16} className="text-red-400 mx-auto" />
                      }
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Intelligence button */}
                        <button
                          onClick={() => handleIntelligence(vendor)}
                          className={`p-2.5 rounded-xl transition-all ${
                            isProfessional
                            ? "text-slate-600 bg-slate-100 hover:text-red-600 hover:bg-red-50"
                            : "text-slate-300 cursor-not-allowed"
                          }`}
                          title={isProfessional ? "Generate Vendor Intelligence" : "Upgrade to Professional"}
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => openEdit(vendor)}
                          className="p-2.5 text-slate-600 bg-slate-100 hover:text-blue-600 hover:bg-blue-50
                            rounded-xl transition-all"
                          title="Edit vendor"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => setDeleteTarget(vendor)}
                          className="p-2.5 text-slate-600 bg-slate-100 hover:text-red-600 hover:bg-red-50
                            rounded-xl transition-all"
                          title="Remove vendor"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── ADD VENDOR MODAL ────────────────────────────────────────── */}
      <AnimatePresence>
        {isAddOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6
              bg-black/60 backdrop-blur-md"
            onClick={() => { setIsAddOpen(false); setAddError(""); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-lg overflow-y-auto max-h-[90vh]"
            >
              {/* Header */}
              <div className="p-9 border-b border-slate-100 flex justify-between
                items-center bg-slate-50/50">
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                  Register New Vendor
                </h2>
                <button onClick={() => { setIsAddOpen(false); setAddError(""); }}
                  className="text-slate-400 hover:text-red-600 transition-colors">
                  <X size={22} />
                </button>
              </div>

              {/* Body */}
              <div className="p-9 space-y-6">
                {/* Name */}
                <div>
                  <label className="text-[9px] font-black uppercase tracking-widest
                    text-slate-400 block mb-2">
                    Legal Entity Name
                  </label>
                  <input
                    value={newVendor.name}
                    onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                    placeholder="e.g. Salesforce, HubSpot, AWS"
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl
                      px-5 py-4 text-sm font-bold text-slate-900
                      placeholder:text-slate-300 placeholder:font-normal
                      focus:outline-none focus:border-red-500 transition-all"
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="text-[9px] font-black uppercase tracking-widest
                    text-slate-400 block mb-2">
                    Vendor Category
                  </label>
                  <select
                    value={newVendor.vendor_type}
                    onChange={(e) => setNewVendor({ ...newVendor, vendor_type: e.target.value })}
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl
                      px-5 py-4 text-sm font-bold text-slate-900 focus:outline-none
                      focus:border-red-500 transition-all appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 1.25rem center",
                      paddingRight: "3rem",
                    }}
                  >
                    {["Software", "Cloud", "Infrastructure", "Payments", "Identity", "Security",
                      "Communication", "Marketing", "Data", "Legal", "SaaS", "Other"
                    ].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Checkboxes */}
                <div>
                  <label className="text-[9px] font-black uppercase tracking-widest
                    text-slate-400 block mb-3">
                    Security Certifications & Status
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "has_soc2",       label: "SOC 2 Certified"   },
                      { key: "has_iso27001",   label: "ISO 27001"         },
                      { key: "gdpr_compliant", label: "GDPR Compliant"    },
                      { key: "no_breach",      label: "No Recent Breaches"},
                    ].map((opt) => {
                      const checked = opt.key === "no_breach"
                        ? !newVendor.recent_breach
                        : newVendor[opt.key as keyof typeof newVendor] as boolean;
                      return (
                        <label
                          key={opt.key}
                          className={`flex items-center gap-3 p-4 rounded-2xl border-2
                            cursor-pointer transition-all ${
                            checked
                              ? "bg-red-50 border-red-200"
                              : "bg-white border-slate-100 hover:border-slate-200"
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-lg flex items-center justify-center
                            transition-all flex-shrink-0 ${
                            checked ? "bg-red-600" : "border-2 border-slate-200"
                          }`}>
                            {checked && <CheckCircle2 size={12} className="text-white" strokeWidth={4} />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={checked}
                            onChange={(e) => {
                              if (opt.key === "no_breach") {
                                setNewVendor({ ...newVendor, recent_breach: !e.target.checked });
                              } else {
                                setNewVendor({ ...newVendor, [opt.key]: e.target.checked });
                              }
                            }}
                          />
                          <span className={`text-[10px] font-black uppercase tracking-widest ${
                            checked ? "text-red-700" : "text-slate-400"
                          }`}>
                            {opt.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* New supply chain fields */}
              <div className="px-9 pb-6 space-y-3">
                <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-2xl
                  border-2 border-slate-100 cursor-pointer hover:border-red-200 transition-all">
                  <input type="checkbox"
                    checked={newVendor.sub_processors_disclosed}
                    onChange={e => setNewVendor({ ...newVendor, sub_processors_disclosed: e.target.checked })}
                    className="w-4 h-4 accent-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-black text-slate-900 uppercase tracking-tight">
                      Sub-Processors Disclosed
                    </p>
                    <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                      Vendor discloses all tools that touch your data
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-2xl
                  border-2 border-slate-100 cursor-pointer hover:border-red-200 transition-all">
                  <input type="checkbox"
                    checked={newVendor.patch_management_policy}
                    onChange={e => setNewVendor({ ...newVendor, patch_management_policy: e.target.checked })}
                    className="w-4 h-4 accent-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-black text-slate-900 uppercase tracking-tight">
                      Patch Management Policy
                    </p>
                    <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                      Vendor has documented vulnerability management process
                    </p>
                  </div>
                </label>

                <div className="p-4 bg-slate-50 rounded-2xl border-2 border-slate-100">
                  <p className="text-xs font-black text-slate-900 uppercase tracking-tight mb-1">
                    Breach Notification SLA
                  </p>
                  <p className="text-[10px] text-slate-400 font-medium mb-3">
                    How quickly will this vendor notify you of a breach?
                  </p>
                  <select
                    value={newVendor.breach_notification_sla}
                    onChange={e => setNewVendor({ ...newVendor, breach_notification_sla: e.target.value })}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm
                      font-bold text-slate-900 focus:outline-none focus:border-red-500"
                  >
                    <option value="unknown">Unknown</option>
                    <option value="24hrs">Within 24 Hours</option>
                    <option value="48hrs">Within 48 Hours</option>
                    <option value="72hrs">Within 72 Hours</option>
                  </select>
                </div>
              </div>

              {addError && (
                <div className="px-9 pb-4">
                  <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-center gap-3">
                    <AlertTriangle size={14} className="text-red-600 flex-shrink-0" />
                    <p className="text-xs font-bold text-red-600">{addError}</p>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="p-9 bg-slate-50 border-t border-slate-100 flex gap-4">
                <button
                  onClick={() => { setIsAddOpen(false); setAddError(""); }}
                  className="flex-1 py-4 font-black text-[10px] uppercase tracking-widest
                    text-slate-400 hover:text-slate-700 transition-colors border-2
                    border-slate-100 rounded-2xl hover:border-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => addMutation.mutate(newVendor)}
                  disabled={!newVendor.name.trim() || addMutation.isPending}
                  className="flex-1 bg-red-600 text-white py-4 rounded-2xl font-black
                    text-[10px] uppercase tracking-widest shadow-lg shadow-red-100
                    hover:bg-slate-900 transition-all disabled:opacity-50
                    disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {addMutation.isPending
                    ? <><Loader2 size={13} className="animate-spin" /> Registering...</>
                    : "Submit for Scoring"
                  }
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── EDIT VENDOR MODAL ────────────────────────────────────────── */}
      <AnimatePresence>
        {editTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6
              bg-black/60 backdrop-blur-md"
            onClick={() => setEditTarget(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-lg p-10"
            >
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">
                Edit Vendor
              </h3>
              <p className="text-xs font-medium text-slate-400 mb-6 uppercase tracking-widest">
                Update certifications and rescore
              </p>

              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Vendor Name</label>
                  <input type="text" value={editVendor.name}
                    onChange={e => setEditVendor({ ...editVendor, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border-2 border-slate-100 text-sm font-medium text-slate-900 focus:outline-none focus:border-red-400" />
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Vendor Type</label>
                  <select value={editVendor.vendor_type}
                    onChange={e => setEditVendor({ ...editVendor, vendor_type: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border-2 border-slate-100 text-sm font-medium text-slate-900 focus:outline-none focus:border-red-400 bg-white">
                    {["Software","Cloud","Infrastructure","Payments","Security","HR","Legal","Identity","Other"].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: "has_soc2",       label: "SOC 2 Certified"    },
                    { key: "has_iso27001",    label: "ISO 27001 Certified"},
                    { key: "gdpr_compliant",  label: "GDPR Compliant"     },
                    { key: "recent_breach",   label: "Recent Breach"      },
                  ].map(({ key, label }) => (
                    <label key={key}
                      className={`flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all
                        ${editVendor[key as keyof typeof editVendor]
                          ? key === "recent_breach"
                            ? "border-red-400 bg-red-50"
                            : "border-emerald-400 bg-emerald-50"
                          : "border-slate-100 bg-slate-50"}`}>
                      <input type="checkbox"
                        checked={!!editVendor[key as keyof typeof editVendor]}
                        onChange={e => setEditVendor({ ...editVendor, [key]: e.target.checked })}
                        className="w-4 h-4 accent-red-600" />
                      <span className="text-xs font-bold text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={() => setEditTarget(null)}
                  className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-400
                    font-black text-xs uppercase tracking-widest hover:border-slate-300 transition-all">
                  Cancel
                </button>
                <button
                  onClick={() => updateMutation.mutate({ id: editTarget.id, payload: editVendor })}
                  disabled={updateMutation.isPending}
                  className="flex-1 py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white
                    font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50">
                  {updateMutation.isPending ? "Saving..." : "Save & Rescore →"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── DELETE CONFIRMATION ──────────────────────────────────────── */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6
              bg-black/60 backdrop-blur-md"
            onClick={() => setDeleteTarget(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-md p-10 text-center"
            >
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center
                justify-center mx-auto mb-5">
                <AlertTriangle size={26} className="text-red-600" />
              </div>
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">
                Remove Vendor
              </h3>
              <p className="text-slate-500 font-medium text-sm leading-relaxed mb-8">
                Are you sure you want to remove{" "}
                <span className="font-black text-slate-900">{deleteTarget.name}</span>{" "}
                from your vendor registry? This cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 py-4 font-black text-[10px] uppercase tracking-widest
                    text-slate-400 border-2 border-slate-100 rounded-2xl
                    hover:border-slate-300 hover:text-slate-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(deleteTarget.id)}
                  disabled={deleteMutation.isPending}
                  className="flex-1 bg-red-600 text-white py-4 rounded-2xl font-black
                    text-[10px] uppercase tracking-widest hover:bg-slate-900 transition-all
                    disabled:opacity-50 flex items-center justify-center gap-2"
>
                  {deleteMutation.isPending
                    ? <><Loader2 size={13} className="animate-spin" /> Removing...</>
                    : <><Trash2 size={13} /> Remove</>
                  }
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── VENDOR INTELLIGENCE MODAL ─────────────────────────────── */}
      <AnimatePresence>
        {intelOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setIntelOpen(false)}
            />
             <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                <div className="bg-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 shadow-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
                        Vendor Intelligence
                      </p>
                      <h2 className="text-2xl font-black text-white tracking-tighter uppercase">
                        {intelTarget?.name}
                      </h2>
                    </div>
                    <button
                      onClick={() => setIntelOpen(false)}
                      className="p-2 text-slate-400 hover:text-white transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                {intelLoading && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={32} className="animate-spin text-red-600" />
                  </div>
                )}

                {!intelLoading && intelData && (
                  <div className="space-y-6">
                    {/* Score */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Risk Score</p>
                        <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
                          intelData.risk_level === 'critical' ? 'bg-red-900/50 text-red-400' :
                          intelData.risk_level === 'high' ? 'bg-amber-900/50 text-amber-400' :
                          intelData.risk_level === 'medium' ? 'bg-blue-900/50 text-blue-400' :
                          'bg-emerald-900/50 text-emerald-400'
                        }`}>{intelData.risk_level}</span>
                      </div>
                      <p className="text-4xl font-black text-white tracking-tighter">
                        {intelData.score}<span className="text-lg text-slate-400">/100</span>
                      </p>
                      <p className="text-slate-400 text-xs font-medium mt-2 leading-relaxed">
                        {intelData.narrative?.[0]}
                      </p>
                    </div>

                    {/* Flag Details */}
                    {intelData.flag_details?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Risk Findings</p>
                        <div className="space-y-3">
                          {intelData.flag_details.map((f: any, i: number) => (
                            <div key={i} className="bg-red-950/30 border border-red-900/30 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-black text-red-400 uppercase tracking-wide">{f.flag}</p>
                                <span className="text-[9px] font-black text-red-500 uppercase">{f.deadline}</span>
                              </div>
                              <p className="text-xs text-slate-400 font-medium mb-2">{f.impact}</p>
                              <p className="text-xs text-white font-bold">→ {f.action}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Regulatory Implications */}
                    {intelData.reg_implications?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Regulatory Implications</p>
                        <div className="space-y-2">
                          {intelData.reg_implications.map((r: string, i: number) => (
                            <div key={i} className="flex items-start gap-2 bg-white/5 rounded-xl p-3">
                              <Shield size={12} className="text-red-500 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-slate-300 font-medium">{r}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contractual Recommendations */}
                    {intelData.contractual?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Contractual Recommendations</p>
                        <div className="space-y-2">
                          {intelData.contractual.map((c: string, i: number) => (
                            <div key={i} className="flex items-start gap-2 bg-white/5 rounded-xl p-3">
                              <ChevronRight size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-slate-300 font-medium">{c}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!intelLoading && !intelData && (
                  <div className="text-center py-8">
                    <p className="text-slate-400 text-sm">Failed to load intelligence. Please try again.</p>
                  </div>
                )}

                {/* Download PDF Button */}
                {intelData && (
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(
                        `${API_BASE}/api/vendors/${intelTarget?.id}/intelligence/pdf`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const blob = await res.blob();
                      const url  = URL.createObjectURL(blob);
                      const a    = document.createElement("a");
                      a.href     = url;
                      a.download = `VicShield_Vendor_Intelligence_${intelTarget?.name}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    className="w-full py-3.5 bg-red-600 text-white rounded-2xl font-black
                      text-[11px] uppercase tracking-widest hover:bg-white hover:text-red-600
                      transition-all flex items-center justify-center gap-2 mt-4"
                  >
                    <FileText size={14} />
                    Download Intelligence PDF
                  </button>
                )}

              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </motion.div>
  );
}